/**
 * LiveFeedProvider - Real-time message state for V2 Dashboard
 *
 * Owns the WebSocket subscription so it persists across page navigations.
 * Exposes a subscriber pattern so consumers (AudioProvider, Live page)
 * receive every message synchronously — React state batching can't swallow them.
 *
 * Channel IDs and enabled state are controlled by AudioProvider (via context).
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { OrgMessage } from '../../common/entities';
import { useMessageSubscription } from '../hooks/useMessageSubscription';
import { useOrg } from './OrgProvider';

type MessageListener = (message: OrgMessage) => void;

interface LiveFeedContextType {
  /** Most recent message received */
  mostRecentMessage: OrgMessage | null;
  /** Update the most recent message */
  setMostRecentMessage: (message: OrgMessage | null) => void;
  /** Subscribe to every message (returns unsubscribe function) */
  subscribe: (listener: MessageListener) => () => void;
  /** WebSocket connection status */
  wsConnected: boolean;
  /** WebSocket error */
  wsError: string | null;
  /** Set channel IDs for the WS subscription */
  setChannelIds: (ids: string[]) => void;
  /** Current channel IDs */
  channelIds: string[];
  /** Enable/disable the WS subscription */
  setWsEnabled: (enabled: boolean) => void;
  /** Update WS channel filter without full reconnect */
  updateWsChannels: (ids: string[]) => void;
}

const LiveFeedContext = createContext<LiveFeedContextType | undefined>(undefined);

interface LiveFeedProviderProps {
  children: React.ReactNode;
}

export function LiveFeedProvider({ children }: LiveFeedProviderProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.org_id;

  const [mostRecentMessage, setMostRecentMessageState] = useState<OrgMessage | null>(null);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [wsEnabled, setWsEnabled] = useState(false);
  const listenersRef = useRef<Set<MessageListener>>(new Set());

  const setMostRecentMessage = useCallback((message: OrgMessage | null) => {
    setMostRecentMessageState(message);
    if (message) {
      listenersRef.current.forEach((fn) => fn(message));
    }
  }, []);

  const subscribe = useCallback((listener: MessageListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const handleWsMessage = useCallback(
    (message: OrgMessage) => {
      setMostRecentMessage(message);
    },
    [setMostRecentMessage]
  );

  const {
    isConnected: wsConnected,
    error: wsError,
    updateChannels: updateWsChannels
  } = useMessageSubscription({
    orgId: orgId || '',
    channelIds,
    onMessage: handleWsMessage,
    enabled: wsEnabled && Boolean(orgId) && channelIds.length > 0
  });

  const value = useMemo<LiveFeedContextType>(
    () => ({
      mostRecentMessage,
      setMostRecentMessage,
      subscribe,
      wsConnected,
      wsError,
      setChannelIds,
      channelIds,
      setWsEnabled,
      updateWsChannels
    }),
    [
      mostRecentMessage,
      setMostRecentMessage,
      subscribe,
      wsConnected,
      wsError,
      channelIds,
      updateWsChannels
    ]
  );

  return <LiveFeedContext.Provider value={value}>{children}</LiveFeedContext.Provider>;
}

/**
 * Hook to access live feed context
 */
export function useLiveFeed(): LiveFeedContextType {
  const context = useContext(LiveFeedContext);
  if (context === undefined) {
    throw new Error('useLiveFeed must be used within a LiveFeedProvider');
  }
  return context;
}

export default LiveFeedProvider;
